'use client';

import React, { useState, useEffect, useRef } from 'react';
import CustomerBottomNav from '@/components/CustomerBottomNav';
import ProgressDots from '@/components/ProgressDots';
import RewardBanner from '@/components/RewardBanner';
import { formatDate, buildQRPayload } from '@/data/mockData';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useLanguage, translations } from '@/hooks/useLanguage';
import { useTheme } from '@/contexts/ThemeContext';
import { fetchCustomerByEmail, subscribeToCustomerSessions } from '@/lib/loyaltyService';
import type { Customer, LoyaltyCard } from '@/data/mockData';

export default function CustomerHomeContent() {
  const { darkMode } = useDarkMode();
  const { lang } = useLanguage();
  const t = translations?.[lang];
  const { theme } = useTheme();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState<number>(0);
  const [qrBrightness, setQrBrightness] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load customer from Supabase on mount
  useEffect(() => {
    const email = typeof window !== 'undefined' ? localStorage.getItem('vq_email') : null;
    if (!email) return;

    const lowerEmail = email.trim().toLowerCase();

    async function loadFromSupabase() {
      const data = await fetchCustomerByEmail(lowerEmail);
      if (data) {
        setCustomer(data);
        // Default to active card, or last card
        const activeIdx = data.loyaltyCards.findIndex(c => c.isActive);
        setSelectedCardIndex(activeIdx >= 0 ? activeIdx : data.loyaltyCards.length - 1);
      } else {
        // Fallback: build from localStorage registration data
        const { getLoggedInCustomerData } = await import('@/data/mockData');
        const local = getLoggedInCustomerData();
        if (local) setCustomer(local);
      }
    }

    loadFromSupabase();

    // Subscribe to real-time updates from Supabase
    const unsubscribe = subscribeToCustomerSessions(lowerEmail, async () => {
      const refreshed = await fetchCustomerByEmail(lowerEmail);
      if (refreshed) {
        setCustomer(refreshed);
        const activeIdx = refreshed.loyaltyCards.findIndex(c => c.isActive);
        setSelectedCardIndex(activeIdx >= 0 ? activeIdx : refreshed.loyaltyCards.length - 1);
      }
    });

    // Also refresh on visibility change (user switches back to app)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const refreshed = await fetchCustomerByEmail(lowerEmail);
        if (refreshed) setCustomer(refreshed);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // The currently viewed card
  const allCards = customer?.loyaltyCards || [];
  const selectedCard: LoyaltyCard | null = allCards[selectedCardIndex] || null;

  // Generate QR code on canvas whenever selected card changes
  useEffect(() => {
    if (!customer || !canvasRef.current) return;

    const payload = buildQRPayload(customer);

    import('qrcode').then((QRCode) => {
      if (!canvasRef.current) return;
      QRCode.toCanvas(canvasRef.current, payload, {
        width: 200,
        margin: 2,
        color: {
          dark: '#1C1C1E',
          light: '#FFFFFF',
        },
        errorCorrectionLevel: 'H',
      }).catch(() => {});
    });
  }, [customer, selectedCardIndex]);

  const sessionCount = selectedCard ? selectedCard?.sessions?.length : 0;

  const nextMilestone = sessionCount < 5 ? 5 : 10;
  const remaining = nextMilestone - sessionCount;

  const show25Banner = selectedCard?.reward25Earned || false;
  const show25Redeemed = selectedCard?.reward25RedeemedAt !== null && selectedCard?.reward25RedeemedAt !== undefined;
  const show50Banner = selectedCard?.reward50Earned || false;
  const show50Redeemed = selectedCard?.reward50RedeemedAt !== null && selectedCard?.reward50RedeemedAt !== undefined;

  const bg = darkMode ? '#1A1A1A' : '#FDFAFC';
  const cardBg = darkMode ? '#2A2A2A' : '#FFFFFF';
  const cardBorder = darkMode ? '#3A3A3A' : theme.preset.accentBorder;
  const textPrimary = darkMode ? '#F5F5F5' : '#1C1C1E';
  const textSecondary = darkMode ? '#A0A0A0' : '#8E6B7A';
  const textMuted = darkMode ? '#666666' : theme.preset.accent;
  const dividerColor = darkMode ? '#3A3A3A' : theme.preset.accentMid;

  const displayFirstName = customer?.name?.split(' ')?.[0] || '';

  return (
    <div className="screen-container" style={{ backgroundColor: bg, transition: 'background-color 300ms ease' }}>
      {/* Header */}
      <div style={{ padding: '56px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: '13px', color: textSecondary, fontWeight: 500, letterSpacing: '0.02em' }}>
              {t?.welcomeBack}
            </p>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: textPrimary, marginTop: '2px' }}>
              {t?.hiName(displayFirstName)}
            </h1>
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '999px',
            backgroundColor: theme.preset.accentLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
          }}>
            {displayFirstName?.charAt(0) || '✦'}
          </div>
        </div>
      </div>

      <div className="content-area" style={{ paddingTop: '28px' }}>

        {/* Multiple Cards Selector — shown only when customer has more than 1 card */}
        {allCards.length > 1 && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px', paddingLeft: '2px' }}>
              Your Cards
            </p>
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
              {allCards.map((card, idx) => (
                <button
                  key={card.id}
                  onClick={() => setSelectedCardIndex(idx)}
                  style={{
                    flexShrink: 0,
                    padding: '8px 14px',
                    borderRadius: '12px',
                    border: `1.5px solid ${selectedCardIndex === idx ? textPrimary : cardBorder}`,
                    backgroundColor: selectedCardIndex === idx ? textPrimary : cardBg,
                    color: selectedCardIndex === idx ? (darkMode ? '#1A1A1A' : '#FFFFFF') : textSecondary,
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 180ms ease',
                    fontFamily: 'var(--font-sans)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '2px',
                    minWidth: '100px',
                  }}
                >
                  <span style={{ fontSize: '11px', opacity: 0.75 }}>
                    {card.isActive ? '● Active' : '✓ Done'}
                  </span>
                  <span>{card.salonName || 'Loyalo Studio'}</span>
                  <span style={{ fontSize: '10px', opacity: 0.65 }}>Card #{card.cardNumber}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* QR Code Card */}
        <div style={{
          backgroundColor: cardBg,
          borderRadius: '20px',
          border: `1px solid ${cardBorder}`,
          padding: '28px 20px',
          textAlign: 'center',
          marginBottom: '20px',
        }}>
          {/* Salon name badge */}
          {selectedCard?.salonName && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: darkMode ? '#3A3A3A' : '#F2F2F2',
              borderRadius: '999px',
              padding: '4px 12px',
              marginBottom: '16px',
            }}>
              <span style={{ fontSize: '12px' }}>✂️</span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: textPrimary, letterSpacing: '0.02em' }}>
                {selectedCard.salonName}
              </span>
            </div>
          )}

          <p style={{ fontSize: '11px', fontWeight: 700, color: textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '20px' }}>{t?.yourLoyaltyQR}</p>

          <div
            style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
            onClick={() => setQrBrightness(!qrBrightness)}
            title="Tap to brighten screen for scanning"
          >
            <div
              style={{
                filter: qrBrightness ? 'brightness(1.15) contrast(1.1)' : 'none',
                transition: 'filter 200ms ease',
                padding: '12px',
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                display: 'inline-block',
              }}
            >
              {customer ? (
                <canvas
                  ref={canvasRef}
                  style={{ display: 'block', borderRadius: '4px' }}
                />
              ) : (
                <div style={{
                  width: '200px',
                  height: '200px',
                  backgroundColor: '#F5F5F5',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '12px', color: '#6B6B6B' }}>Loading…</span>
                </div>
              )}
            </div>
          </div>

          <p style={{ fontSize: '12px', color: textSecondary, marginTop: '14px' }}>
            {t?.tapToBrighten}
          </p>
          <p style={{ fontSize: '11px', color: textMuted, marginTop: '4px', letterSpacing: '0.04em' }}>
            ID: {customer?.id?.toUpperCase()}
          </p>
        </div>

        {/* Progress Section */}
        {selectedCard && (
          <div style={{
            backgroundColor: cardBg,
            borderRadius: '20px',
            border: `1px solid ${cardBorder}`,
            padding: '22px 20px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t?.yourLoyaltyProgress}</p>
              <span style={{ fontSize: '13px', color: textSecondary }}>
                {t?.card} #{selectedCard?.cardNumber}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '14px' }}>
              <span style={{ fontSize: '36px', fontWeight: 700, color: textPrimary, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                {sessionCount}
              </span>
              <span style={{ fontSize: '20px', fontWeight: 400, color: textMuted }}>/</span>
              <span style={{ fontSize: '20px', fontWeight: 500, color: textMuted }}>10</span>
            </div>

            <ProgressDots filled={sessionCount} total={10} size="md" />

            {sessionCount < 10 && selectedCard.isActive && (
              <p style={{ fontSize: '14px', color: textSecondary, marginTop: '14px', fontWeight: 500 }}>
                {t?.moreVisits(remaining, nextMilestone === 5 ? '25%' : '50%')}
              </p>
            )}

            {sessionCount === 10 && (
              <p style={{ fontSize: '14px', color: textPrimary, marginTop: '14px', fontWeight: 600 }}>
                {t?.allRewardsUnlocked}
              </p>
            )}

            {!selectedCard.isActive && sessionCount < 10 && (
              <p style={{ fontSize: '13px', color: textMuted, marginTop: '14px' }}>
                This card is completed.
              </p>
            )}

            <div style={{ marginTop: '8px', paddingTop: '14px', borderTop: `1px solid ${dividerColor}` }}>
              <p style={{ fontSize: '12px', color: textMuted }}>
                {t?.cardStarted} {formatDate(selectedCard?.startDate, lang)}
              </p>
            </div>
          </div>
        )}

        {/* Rewards */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px', paddingLeft: '2px' }}>{t?.yourRewards}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <RewardBanner
              discount="25%"
              available={show25Banner && !show25Redeemed}
              redeemed={show25Redeemed}
              redeemedAt={selectedCard?.reward25RedeemedAt ? formatDate(selectedCard?.reward25RedeemedAt, lang) : undefined}
            />
            <RewardBanner
              discount="50%"
              available={show50Banner && !show50Redeemed}
              redeemed={show50Redeemed}
              redeemedAt={selectedCard?.reward50RedeemedAt ? formatDate(selectedCard?.reward50RedeemedAt, lang) : undefined}
            />
          </div>
        </div>

        {/* Salon Info */}
        <div style={{ textAlign: 'center', paddingTop: '8px', paddingBottom: '8px' }}>
          <p style={{ fontSize: '13px', color: textMuted, letterSpacing: '0.06em', fontWeight: 500 }}>
            {t?.vqstudio}
          </p>
        </div>
      </div>

      <CustomerBottomNav />
    </div>
  );
}